import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, X, LogOut, Code2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useActiveSection } from '../../hooks/useActiveSection';
import { smoothScrollTo } from '../../utils/smoothScroll';
import '../../styles/components/Navbar.css';

const NAV_ITEMS = [
  { id: 'hero', key: 'nav.home' },
  { id: 'about', key: 'nav.about' },
  { id: 'team', key: 'nav.team' },
  { id: 'skills', key: 'nav.skills' },
  { id: 'projects', key: 'nav.projects' },
  { id: 'contact', key: 'nav.contact' },
];

interface NavbarProps {
  onOpenSnippet?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onOpenSnippet }) => {
  const { t } = useLanguage();
  const { isLoggedIn, logout } = useAuth();
  const activeSection = useActiveSection();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navPillRef = useRef<HTMLDivElement>(null);
  const linkRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});

  // Update the sliding indicator position based on active section
  const updateIndicator = useCallback(() => {
    const activeEl = linkRefs.current.get(activeSection);
    const pill = navPillRef.current;
    if (activeEl && pill) {
      const pillRect = pill.getBoundingClientRect();
      const linkRect = activeEl.getBoundingClientRect();
      setIndicatorStyle({
        left: linkRect.left - pillRect.left,
        width: linkRect.width,
        height: linkRect.height,
        opacity: 1,
      });
    }
  }, [activeSection]);

  useEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  // Also update on window resize
  useEffect(() => {
    const handleResize = () => updateIndicator();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateIndicator]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      smoothScrollTo(top, 700);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`navbar ${isScrolled ? 'navbar--scrolled' : ''}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="navbar__container container">
        <a href="#hero" className="navbar__logo" onClick={(e) => { e.preventDefault(); scrollToSection('hero'); }}>
          <span className="navbar__logo-text">Famini<span className="navbar__logo-plus">+</span></span>
        </a>

        <div className="navbar__right">
          <div ref={navPillRef} className={`navbar__nav-pill ${isMobileMenuOpen ? 'navbar__nav-pill--open' : ''}`}>
            {/* Sliding active indicator */}
            <div
              className="navbar__indicator"
              style={indicatorStyle}
              aria-hidden="true"
            />
            {NAV_ITEMS.map((item, index) => (
              <button
                key={item.id}
                ref={(el) => { if (el) linkRefs.current.set(item.id, el); }}
                className={`navbar__link ${activeSection === item.id ? 'navbar__link--active' : ''}`}
                onClick={() => scrollToSection(item.id)}
                style={{ animationDelay: isMobileMenuOpen ? `${index * 50}ms` : undefined }}
              >
                {t(item.key)}
              </button>
            ))}

            {isLoggedIn && (
              <button className="navbar__link navbar__link--logout" onClick={logout} aria-label={t('nav.logout')}>
                <LogOut size={16} />
                <span className="navbar__link--logout-text">{t('nav.logout')}</span>
              </button>
            )}

          </div>

          <div className="navbar__actions">
            {onOpenSnippet && (
              <button
                className="navbar__snippet-btn"
                onClick={onOpenSnippet}
                aria-label="Open Snippet Share"
                title="Snippet Share"
              >
                <Code2 size={16} />
                <span>Snippet</span>
              </button>
            )}

            {isLoggedIn && (
              <button className="navbar__logout-btn" onClick={logout} aria-label={t('nav.logout')} title={t('nav.logout')}>
                <LogOut size={18} />
              </button>
            )}

            <button
              className={`navbar__hamburger ${isMobileMenuOpen ? 'navbar__hamburger--open' : ''}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
